import jsPDF from 'jspdf'
import { PlusJakartaSansRegular } from '../../assets/fonts/PlusJakartaSans/PlusJakartaSans-Regular-normal'
import { PlusJakartaSansBold } from '../../assets/fonts/PlusJakartaSans/PlusJakartaSans-Bold-bold'
import React, { useCallback } from 'react'
import ZocksLogoFullBlack from '../../assets/images/ZocksLogoFullBlack.png'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { useLazyGetAccountQuery } from '../../store/accountUserSettings/accountUserSettings'
import { useLazyDownloadGlobalFileRequestQuery } from '../../store/media/file'
import { useEffectiveConfig } from '../../common/useEffectiveConfig'
import * as Sentry from '@sentry/react'

const loadImageFromUrl = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous' // Important for CORS
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = url
  })
}

const LOGO_TARGET_DPI = 600
const PX_PER_INCH_AT_DOC_SCALE = 54
const LOGO_JPEG_QUALITY = 0.92
const PAGE_FORMAT = 'letter'
export const PRINT_SAFE_EDGE_OFFSET = PX_PER_INCH_AT_DOC_SCALE / 2
const MAX_LOGO_WIDTH = 400
const MAX_LOGO_HEIGHT = 40
const FALLBACK_LOGO_WIDTH = 104
const LOGO_CONTENT_GAP = 8
const CONTENT_TOP_MARGIN = PRINT_SAFE_EDGE_OFFSET + MAX_LOGO_HEIGHT + LOGO_CONTENT_GAP
export const COMPLIANCE_FONT_SIZE = 8

export const getLogoRasterSize = (sourceWidth: number, sourceHeight: number, boxWidth: number, boxHeight: number): { width: number; height: number } | null => {
  if (sourceWidth <= 0 || sourceHeight <= 0 || boxWidth <= 0 || boxHeight <= 0) {
    return null
  }

  const targetWidth = (boxWidth / PX_PER_INCH_AT_DOC_SCALE) * LOGO_TARGET_DPI
  const targetHeight = (boxHeight / PX_PER_INCH_AT_DOC_SCALE) * LOGO_TARGET_DPI
  const scale = Math.min(targetWidth / sourceWidth, targetHeight / sourceHeight, 1)

  if (scale >= 1) {
    return null
  }

  return { width: Math.max(1, Math.round(sourceWidth * scale)), height: Math.max(1, Math.round(sourceHeight * scale)) }
}

export const getLogoRasterFormat = (extension: string): { mimeType: string; format: string } => {
  return extension === 'jpeg' ? { mimeType: 'image/jpeg', format: 'jpeg' } : { mimeType: 'image/png', format: 'png' }
}

const rasterizeLogo = (image: HTMLImageElement, width: number, height: number, mimeType: string): string | null => {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height

  const context = canvas.getContext('2d')
  if (!context) {
    return null
  }

  context.imageSmoothingQuality = 'high'
  context.drawImage(image, 0, 0, width, height)

  try {
    return canvas.toDataURL(mimeType, LOGO_JPEG_QUALITY)
  } catch {
    return null
  }
}

const rasterizeLogoForBox = (image: HTMLImageElement, boxWidth: number, boxHeight: number, extension: string): { dataUrl: string; format: string } | null => {
  const rasterSize = getLogoRasterSize(image.naturalWidth, image.naturalHeight, boxWidth, boxHeight)

  if (!rasterSize) {
    return null
  }

  const { mimeType, format } = getLogoRasterFormat(extension)
  const dataUrl = rasterizeLogo(image, rasterSize.width, rasterSize.height, mimeType)

  return dataUrl ? { dataUrl, format } : null
}

const contentTypeToExtension = (contentType: string): string => {
  switch (contentType) {
    case 'image/jpeg':
    case 'image/jpg':
      return 'jpeg'
    case 'image/png':
      return 'png'
    case 'image/gif':
      return 'gif'
    default:
      throw new Error(`Unsupported logo type: ${contentType}`)
  }
}

export const useExportToPDF = (elementToExport: React.RefObject<HTMLElement> | null, pdfStyles: string = '', pdfName: string = 'zocks-export.pdf') => {
  const accountId = useAppSelector(selectAccountId)
  const [triggerGetAccount] = useLazyGetAccountQuery()
  const [downloadFileRequest] = useLazyDownloadGlobalFileRequestQuery()
  const { pdfComplianceMessage } = useEffectiveConfig()

  const getLogoData = useCallback(async (): Promise<{ location: string; contentType: string } | null> => {
    const { data } = await triggerGetAccount({ accountId }).unwrap()

    const fileId = data.effectiveConfig.assets?.logo?.logoFile?.fileId

    if (!fileId) {
      return null
    }

    const {
      data: { location, fileInfo },
    } = await downloadFileRequest({ accountId, fileId }).unwrap()

    return { location, contentType: fileInfo.contentType }
  }, [triggerGetAccount, downloadFileRequest, accountId])

  const generatePDF = useCallback(async (): Promise<jsPDF | null> => {
    if (!elementToExport || !elementToExport.current) {
      return null
    }

    const logoData = await getLogoData()

    const extension = logoData?.contentType ? contentTypeToExtension(logoData.contentType) : 'png'
    let logoToUse: string | HTMLImageElement = ZocksLogoFullBlack
    if (logoData) {
      try {
        logoToUse = await loadImageFromUrl(logoData.location)
      } catch (error) {
        console.warn('Failed to load logo from URL, using fallback:', error)
      }
    }

    const doc = new jsPDF({ unit: 'px', format: PAGE_FORMAT, compress: true })

    const uniqueId = `pdf-container-${Date.now()}`

    const clonedSection = elementToExport.current.cloneNode(true) as HTMLElement
    clonedSection.classList.add(uniqueId)

    doc.addFileToVFS('PlusJakartaSans-Regular.ttf', PlusJakartaSansRegular)
    doc.addFont('PlusJakartaSans-Regular.ttf', 'PlusJakartaSans', 'normal')

    doc.addFileToVFS('PlusJakartaSans-Bold.ttf', PlusJakartaSansBold)
    doc.addFont('PlusJakartaSans-Bold.ttf', 'PlusJakartaSans', 'bold')

    const style = document.createElement('style')
    style.innerHTML = `
       @font-face {
        font-family: 'PlusJakartaSans';
        src: url('data:font/truetype;charset=utf-8;base64,${PlusJakartaSansRegular}') format('truetype');
        font-weight: normal;
      }
      @font-face {
        font-family: 'PlusJakartaSans';
        src: url('data:font/truetype;charset=utf-8;base64,${PlusJakartaSansBold}') format('truetype');
        font-weight: bold;
      }

      .${uniqueId} {
        width: 76% !important;
        font-family: 'PlusJakartaSans';

        ul {
          list-style: none !important;
          padding-left: 0 !important;
          margin-bottom: 5px !important;
        }

        ul li {
          position: relative !important;
          padding-left: 20px !important;
        }

        ul li:before {
          content: "•" !important;
          position: absolute !important;
          left: 0 !important;
          top: 0 !important;
          color: black !important;
        }

        /* Hide bullet for nested list items, matching the web CSS */
        .lexical-nested-listitem {
          list-style-type: none !important;
        }
        ul li.lexical-nested-listitem:before {
          content: none !important;
        }

        .bi {
          display: none !important;
        }

        .collapse {
          display: block !important;
          height: auto !important;
          opacity: 1 !important;
          visibility: visible !important;
        }

        ul + p {
          margin-top: 20px !important;
        }

        ${pdfStyles}
      }
    `

    clonedSection.prepend(style)

    let imgWidth = FALLBACK_LOGO_WIDTH
    let imgHeight = MAX_LOGO_HEIGHT

    if (logoToUse instanceof HTMLImageElement) {
      const aspectRatio = logoToUse.naturalWidth / logoToUse.naturalHeight
      imgHeight = MAX_LOGO_HEIGHT
      imgWidth = imgHeight * aspectRatio
      if (imgWidth > MAX_LOGO_WIDTH) {
        imgWidth = MAX_LOGO_WIDTH
        imgHeight = imgWidth / aspectRatio
      }
    }

    const rasterizedLogo = logoToUse instanceof HTMLImageElement ? rasterizeLogoForBox(logoToUse, imgWidth, imgHeight, extension) : null
    const logoImage = rasterizedLogo?.dataUrl ?? logoToUse
    const logoFormat = rasterizedLogo?.format ?? extension

    const pageWidth = doc.internal.pageSize.getWidth()
    const imgX = pageWidth - imgWidth - PRINT_SAFE_EDGE_OFFSET
    const imgY = PRINT_SAFE_EDGE_OFFSET

    const inputs = clonedSection.querySelectorAll('input')
    inputs.forEach((input) => {
      const span = document.createElement('span')
      span.textContent = ['checkbox', 'radio'].includes(input.type) ? (input.checked ? 'Yes' : 'No') : input.value
      span.style.textAlign = 'justify'

      input.parentNode?.replaceChild(span, input)
    })

    // FLATTEN ul > li > div > p > span[data-lexical-text] to ul > li (text)
    const flattenLexicalLists = (root: HTMLElement) => {
      const lists = root.querySelectorAll('ul')
      lists.forEach((list) => {
        list.querySelectorAll('li').forEach((li) => {
          // Check for the specific structure
          const div = li.querySelector('div[data-lexical-editor]')
          if (div) {
            const span = div.querySelector('span[data-lexical-text]')
            if (span) {
              li.textContent = span.textContent || ''
            }
          }
        })
      })
    }
    flattenLexicalLists(clonedSection)

    const footerMargin = PRINT_SAFE_EDGE_OFFSET
    const complianceLineHeight = 10
    const pageHeight = doc.internal.pageSize.getHeight()
    const complianceMaxWidth = pageWidth - 80

    const complianceBlockHeight = (lineCount: number): number => lineCount * complianceLineHeight + COMPLIANCE_FONT_SIZE

    const calculateComplianceHeight = (complianceText: string): number => {
      doc.setFont('PlusJakartaSans', 'normal')
      doc.setFontSize(COMPLIANCE_FONT_SIZE)
      const wrappedText = doc.splitTextToSize(complianceText, complianceMaxWidth)
      return complianceBlockHeight(wrappedText.length) + footerMargin
    }

    const complianceHeight = pdfComplianceMessage ? calculateComplianceHeight(pdfComplianceMessage) : 0
    const bottomMargin = 40 + complianceHeight

    const addComplianceMessage = (pdf: jsPDF, complianceText: string) => {
      pdf.setFont('PlusJakartaSans', 'normal')
      pdf.setFontSize(COMPLIANCE_FONT_SIZE)
      pdf.setTextColor(150)

      const wrappedText = pdf.splitTextToSize(complianceText, complianceMaxWidth)
      const lastBaselineY = pageHeight - footerMargin - COMPLIANCE_FONT_SIZE
      const firstBaselineY = lastBaselineY - (wrappedText.length - 1) * complianceLineHeight

      wrappedText.forEach((line: string, idx: number) => {
        pdf.text(line, pageWidth / 2, firstBaselineY + idx * complianceLineHeight, { align: 'center' })
      })
    }

    return new Promise<jsPDF>((resolve, reject) => {
      doc
        .html(clonedSection, {
          callback: function (pdf) {
            const totalPages = pdf.getNumberOfPages()
            Array.from({ length: totalPages }, (_, i) => i + 1).forEach((page) => {
              pdf.setPage(page)
              doc.addImage(logoImage, logoFormat, imgX, imgY, imgWidth, imgHeight)
              if (pdfComplianceMessage) {
                try {
                  addComplianceMessage(pdf, pdfComplianceMessage)
                } catch (error) {
                  Sentry.captureException(error)
                }
              }
            })
            resolve(pdf)
          },
          html2canvas: {
            backgroundColor: '#ffffff',
            ignoreElements: (el) => {
              return el.classList.contains('ignore-in-pdf') || ['SVG', 'BUTTON', 'svg'].includes(el.tagName)
            },
          },
          autoPaging: 'text',
          margin: [CONTENT_TOP_MARGIN, 40, bottomMargin, 40],
          width: 480,
          windowWidth: 960,
        })
        .catch(reject)
    })
  }, [elementToExport, pdfStyles, pdfComplianceMessage, getLogoData])

  const exportToPDF = useCallback(
    async (name?: string) => {
      try {
        const pdf = await generatePDF()
        if (pdf) {
          pdf.save(name ?? pdfName)
        }
      } catch (error) {
        Sentry.captureException(error)
      }
    },
    [generatePDF, pdfName],
  )

  const generatePDFBlob = useCallback(async (): Promise<Blob | null> => {
    const pdf = await generatePDF()
    if (!pdf) {
      return null
    }
    return pdf.output('blob')
  }, [generatePDF])

  return {
    exportToPDF,
    generatePDFBlob,
  }
}
